import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import {
  useLoad,
  useTriggerLoad,
  useTriggerLoadWithInput,
} from "@redotech/react-util/load";
import { RedoShopifyDeliveryMethodType } from "@redotech/redo-fulfillment-common/fulfillment-delivery-method-type";
import {
  canPurchaseLabelStatus,
  FulfillmentGroupStatus,
  isDoneStatus,
  isEditingLockedStatus,
} from "@redotech/redo-fulfillment-common/fulfillment-group-status";
import { FulfillmentOrderAddress } from "@redotech/redo-fulfillment-customs-common/fulfillment-order-address";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { OMSUser } from "@redotech/redo-fulfillment-group-common/oms-user";
import { PrintableDocument } from "@redotech/redo-fulfillment-group-common/print-job";
import { FulfillmentGroupActions } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import {
  OutboundParcel,
  Parcel,
} from "@redotech/redo-fulfillment-parcel-common/parcel";
import TriangleRightIcon from "@redotech/redo-icons/play_right_filled.svg";
import TriangleDownIcon from "@redotech/redo-icons/triangle-down_filled.svg";
import XIcon from "@redotech/redo-icons/x-close.svg";
import {
  FreightBookingModal,
  fulfillmentGroupToFreightProducts,
} from "@redotech/redo-merchant-app-common/freight/freight-booking-modal";
import { RedoInventoryRpcClientContext } from "@redotech/redo-merchant-app-common/inventory-rpc-client-provider";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";
import type { PurchaseFreightBookingResponse } from "@redotech/redo-model/freight/freight-booking";
import {
  canVoidFreight,
  canVoidLabel,
} from "@redotech/redo-model/fulfillments/fulfillment-group-utils";
import { personalizedSlipsAccessible } from "@redotech/redo-model/personalized-slip/personalized-slip-access";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { IconButton } from "@redotech/redo-web/button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import {
  memo,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import DraggableList from "react-draggable-list";
import { useNavigate } from "react-router-dom";
import { AddFundsModalContext } from "../common/add-funds";
import { AdditionalFulfillmentSettings } from "../common/additional-fulfillment-settings";
import {
  BinSelectionModal,
  InventoryAdjustmentWarningModal,
  useBinSelectionModal,
} from "../common/bin-selection-modal";
import { CancelOrderModalContext } from "../common/cancel-order/cancel-order-context";
import { CarrierAccountsProvider } from "../common/carrier-accounts-context";
import { CreateReturnLabelModalContext } from "../common/create-return-label/create-return-label-context";
import { FulfillmentCustomsDeclarationsCard } from "../common/customs-declarations";
import { EditAddressModalContext } from "../common/edit-address/edit-address-context";
import { EditManualOrderSlideout } from "../common/edit-manual-order-slideout";
import { doOMSAction } from "../common/fulfillment-group-actions";
import {
  InsufficientInventoryModal,
  useInsufficientInventoryModal,
} from "../common/insufficient-inventory-modal";
import { MarkAsFulfilledModalContext } from "../common/mark-as-fulfilled/mark-as-fulfilled-context";
import { MissingItemsModalContext } from "../common/missing-items/missing-items-modal-context";
import { PackingSlipBinModalContext } from "../common/packing-slip-bin-modal-provider";
import {
  ParcelDetails,
  ParcelDetailsHeaderActions,
} from "../common/parcels/parcel-details";
import { ParcelEditProvider } from "../common/parcels/parcel-edit-context";
import {
  PinVerificationProvider,
  PinVerificationType,
  usePinVerification,
} from "../common/pin-verification-context";
import { PlaceOnHoldModalContext } from "../common/place-on-hold/place-on-hold-context";
import {
  getDocumentPrintStatusMapForFulfillmentGroups,
  PackingSlipTemplatesContext,
  PersonalizedSlipTemplatesContext,
  PrintBatchContext,
} from "../common/print-batch-modal";
import { SubmitShippingClaimModalContext } from "../common/shipping-claim/shipping-claim-context";
import { UnmergeModal } from "../common/unmerge-modal";
import { UserSelectDropdown } from "../common/user-select-dropdown";
import { actionMessages, calculateNeedsFunds } from "../common/util";
import { useConfirmEditAddress } from "../hooks/use-confirm-edit-address";
import { useDeviceLocation } from "../hooks/use-device-location";
import { usePresetSelection } from "../hooks/use-preset-selection";
import {
  useSelectedPackingSlipSizes,
  useSelectedPersonalizedSlipSizes,
} from "../hooks/use-selected-slip-sizes";
import { useSlideoutConfig } from "../hooks/use-slideout-config";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import { ParcelPhotosViewer } from "../parcel-photos/parcel-photos-viewer";
import { isConsolidationShipment } from "../utils/consolidation-invoice-utils";
import { useGetRequestedShippingServicePromisedDeliveryDate } from "../utils/get-shipping-date-promise";
import { getCustomerShippingMethod } from "../utils/outbound-labels-utils";
import {
  useFulfillmentGroupUsedLearning,
  useGroupInfo,
} from "../utils/use-group-info";
import { useParcelActions } from "../utils/use-parcel-actions";
import { useRateActions } from "../utils/use-rate-actions";
import { ConfirmEditAddressModal } from "./confirm-edit-address-modal";
import {
  DraggableFulfillmentCard,
  FulfillmentCardData,
} from "./draggable-fulfillment-card";
import { FulfillmentActionsCard } from "./fulfillment-group-slideout/actions";
import { FulfillmentNotesSlideout } from "./fulfillment-group-slideout/fulfillment-notes-slideout";
import {
  FulfillmentItemsCard,
  FulfillmentItemsCardHeaderActions,
} from "./fulfillment-group-slideout/items";
import { FulfillmentOrderInformationCard } from "./fulfillment-group-slideout/order-information";
import { FulfillmentPackingSlipTemplateCard } from "./fulfillment-group-slideout/packing-slip-template";
import { FulfillmentPersonalizedSlipTemplateCard } from "./fulfillment-group-slideout/personalized-slip-template";
import { ShipmentMessagesCard } from "./fulfillment-group-slideout/shipment-messages-card";
import { FulfillmentShippingInfoCard } from "./fulfillment-group-slideout/shipping-info";
import * as ordersCss from "./outbound-labels.module.css";
import { MarketplaceNotificationSection } from "./outbound-order/marketplace-notification";
import { OverrideNotReceivedModal } from "./override-not-received-modal";

export const FulfillmentForm = memo(function FulfillmentGroupForm({
  fulfillmentGroups,
  onClose,
  parcelValues,
  updateRows,
  updatesPending,
  balance,
  balanceLoading,
  availableUsers,
  reloadBalance,
  resetSelections,
  printShortcutsDisabled,
  onActionButtonClick,
  externalActionButtonRef,
  triggerRefresh,
  onGroupsFulfillment,
  onGroupsVoided,
  setRows,
  setSelectedFulfillmentGroups,
  setSearchRefreshSymbol,
  inert,
  onChangeLocations,
  showHeader = true,
  permissionsToEditEveryGroup,
  useDefaultActionButton = false,
  deselectGroups,
  workstationDefaultPresetId,
  showSingleParcelTrackingNumber = false,
  hiddenCardIds = [],
  showActionCostSummary = false,
}: {
  fulfillmentGroups: readonly FulfillmentGroupData[];
  onClose?: () => void;
  parcelValues: readonly Parcel[];
  updateRows: (updates: FulfillmentGroupUpdate[]) => void;
  updatesPending: boolean;
  balance: string | undefined;
  balanceLoading: boolean;
  availableUsers: OMSUser[];
  printShortcutsDisabled?: boolean;
  onActionButtonClick?: () => void;
  externalActionButtonRef?: (ref: HTMLButtonElement | null) => void;
  reloadBalance: () => void;
  resetSelections: () => void;
  triggerRefresh: () => void;
  onGroupsFulfillment: (groupIds: string[]) => void;
  onGroupsVoided: (groups: string[]) => void;
  setRows?: (value: React.SetStateAction<FulfillmentGroupData[]>) => void;
  setSelectedFulfillmentGroups?: (groups: FulfillmentGroupData[]) => void;
  setSearchRefreshSymbol?: (value: symbol) => void;
  inert?: boolean;
  onChangeLocations: (groups: string[]) => void;
  showHeader?: boolean;
  permissionsToEditEveryGroup: boolean;
  useDefaultActionButton?: boolean;
  deselectGroups?: (groupIds: string[]) => void;
  workstationDefaultPresetId?: string;
  showSingleParcelTrackingNumber?: boolean;
  hiddenCardIds?: readonly string[];
  showActionCostSummary?: boolean;
}) {
  const [deviceLocationId] = useDeviceLocation();
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const inventoryRpcClient = useContext(RedoInventoryRpcClientContext);
  const team = useContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);
  const user = useContext(UserContext);
  const batchPrinting = useContext(PrintBatchContext);
  const packingSlips = useContext(PackingSlipTemplatesContext);
  const personalizedSlips = useContext(PersonalizedSlipTemplatesContext);
  const openMarkAsFulfilledModal = useContext(MarkAsFulfilledModalContext);
  const openCancelOrderModal = useContext(CancelOrderModalContext);
  const openPackingSlipBinModal = useContext(PackingSlipBinModalContext);
  const missingItemsModal = useContext(MissingItemsModalContext);
  const openEditAddressModal = useContext(EditAddressModalContext);
  const { executeWithPinCheck } = usePinVerification();

  const navigate = useNavigate();

  const [editOrderSlideoutOpen, setEditOrderSlideoutOpen] = useState(false);
  const [fulfillmentDate, setFulfillmentDate] = useState<Date | null>(null);
  const [notifyMarketplace, setNotifyMarketplace] = useState<boolean>(true);
  const [overrideNotReceivedModalOpen, setOverrideNotReceivedModalOpen] =
    useState(false);
  const [unmergeModalOpen, setUnmergeModalOpen] = useState(false);
  const [freightBookingModalOpen, setFreightBookingModalOpen] = useState(false);
  const [parcelModalOpen, setParcelModalOpen] = useState(false);

  const {
    modalProps: binSelectionModalProps,
    inventoryAdjustmentWarningModalProps,
    openBinSelectionModal,
    openInventoryAdjustmentWarningModal,
  } = useBinSelectionModal();
  const { insufficientInventoryModalProps, openInsufficientInventoryModal } =
    useInsufficientInventoryModal();

  const handleFreightPurchase = useHandler(
    async (response: PurchaseFreightBookingResponse) => {
      const groupId = fulfillmentGroups[0]?._id;
      if (!groupId) {
        return;
      }
      await client.fulfillFulfillmentGroupWithFreightBooking({
        fulfillmentGroupId: groupId,
        freightBookingId: response.freightBookingId,
      });
      batchPrinting.printBatch({
        document: PrintableDocument.BILL_OF_LADING,
        batch: { fulfillmentGroupIds: [groupId] },
        documentPrintStatusMap:
          getDocumentPrintStatusMapForFulfillmentGroups(fulfillmentGroups),
      });
    },
  );

  const { cardPreferences, setCardPreferences } = useSlideoutConfig();
  const cardOrder = cardPreferences.order;
  const hiddenCardIdSet = useMemo(
    () => new Set(hiddenCardIds),
    [hiddenCardIds],
  );

  const handleCardReorder = useHandler(
    (newList: readonly FulfillmentCardData[]) => {
      const newOrder = newList.map((card) => card.id);
      setCardPreferences({ ...cardPreferences, order: newOrder });
    },
  );

  const handleToggleExpanded = useHandler((cardId: string) => {
    setCardPreferences({
      ...cardPreferences,
      collapsedStates: {
        ...cardPreferences.collapsedStates,
        [cardId]: !cardPreferences.collapsedStates[cardId],
      },
    });
  });

  const {
    confirmEditAddressModalAddress,
    onEditAddress,
    onConfirmEditAddress,
    onUseAutoCleanedAddress,
    closeConfirmModal,
  } = useConfirmEditAddress({ fulfillmentGroups, client, triggerRefresh });

  const presets = useLoad(async () => {
    const result = (await client.getPresets({})).presets;
    return result;
  }, []);

  const { selectedPreset, setSelectedPreset, onPresetSelect } =
    usePresetSelection(fulfillmentGroups, updateRows);

  const appliedPresetToGroupsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (
      !workstationDefaultPresetId ||
      !presets.value ||
      !fulfillmentGroups.length
    ) {
      return;
    }

    const groupIds = fulfillmentGroups.map((g) => g._id);
    const alreadyApplied = groupIds.every((id) =>
      appliedPresetToGroupsRef.current.has(id),
    );
    if (alreadyApplied) {
      return;
    }

    const preset = presets.value.find(
      (p) => p._id === workstationDefaultPresetId,
    );
    if (preset) {
      onPresetSelect(preset);
      for (const id of groupIds) {
        appliedPresetToGroupsRef.current.add(id);
      }
    }
  }, [
    workstationDefaultPresetId,
    presets.value,
    fulfillmentGroups,
    onPresetSelect,
  ]);

  const everythingDisabled = !permissionsToEditEveryGroup;

  const { onParcelChange, onParcelAdd, onRemoveParcel, onAllocationsFlush } =
    useParcelActions({ fulfillmentGroups, updateRows, setSelectedPreset });

  const [parcelToBeEdited, setParcelToBeEdited] = useState<number>(0);
  const updateParcelBeingEdited = useHandler(
    (parcel: Partial<OutboundParcel>) => {
      onParcelChange(parcel, parcelToBeEdited);
    },
  );

  const fulfillmentGroupIds = useMemo(
    () => fulfillmentGroups.map((group) => group._id).join(","),
    [fulfillmentGroups],
  );
  useEffect(() => {
    setParcelToBeEdited(0);
  }, [fulfillmentGroupIds]);

  const onUserChange = useHandler((user: OMSUser | null) => {
    updateRows(
      fulfillmentGroups.map(({ _id }) => ({
        fulfillmentGroupId: _id,
        assignedUser: user,
      })),
    );
  });

  const onSelectedLocationChange = useHandler(
    async (externalLocationId: string) => {
      try {
        updateRows(
          fulfillmentGroups.map((group) => ({
            fulfillmentGroupId: group._id,
            status: FulfillmentGroupStatus.Pending,
          })),
        );
        const { success } = await client.moveFulfillmentGroups({
          fulfillmentGroupIds: fulfillmentGroups.map((group) => group._id),
          externalLocationId,
        });
        if (success) {
          onChangeLocations(fulfillmentGroups.map((group) => group._id));
          toast("Changing location...", {
            variant: "success",
            duration: Temporal.Duration.from({ seconds: 2 }),
          });
        } else {
          toast("Error changing location", {
            variant: "error",
            duration: Temporal.Duration.from({ seconds: 2 }),
          });
        }
      } catch (e) {
        toast("Error changing location", {
          variant: "error",
          duration: Temporal.Duration.from({ seconds: 2 }),
        });
      }
    },
  );

  const placeOnHold = useContext(PlaceOnHoldModalContext);
  const createReturnLabel = useContext(CreateReturnLabelModalContext);
  const onPlaceOnHold = useHandler(() => {
    placeOnHold(fulfillmentGroups.map((group) => group._id));
  });

  const addFunds = useContext(AddFundsModalContext);
  const onAddFunds = useHandler(() => {
    addFunds({
      labelPrice: totalRatePrice ?? 0,
      balance,
      balanceLoading,
      onSuccess: onAddFundsCompleted,
    });
  });

  const onAddFundsCompleted = useHandler(async () => {
    performAction(FulfillmentGroupActions.CREATE_AND_PRINT_LABEL);
  });

  const onEditManualOrder = useHandler(() => {
    setEditOrderSlideoutOpen(true);
  });

  const submitShippingClaim = useContext(SubmitShippingClaimModalContext);
  const onSubmitCarrierClaim = useHandler(() => {
    if (fulfillmentGroups.length === 1 && fulfillmentGroups[0]) {
      submitShippingClaim(fulfillmentGroups[0]);
    }
  });

  const selectedStatus = useMemo(() => {
    const allStatuses = fulfillmentGroups.map((group) => group.status);
    return allStatuses.every((status) => allStatuses[0] === status)
      ? allStatuses[0]
      : null;
  }, [fulfillmentGroups]);

  const canPurchaseEveryGroup = useMemo(() => {
    const allStatuses = fulfillmentGroups.map((group) => group.status);
    return allStatuses.every((status) => canPurchaseLabelStatus(status));
  }, [fulfillmentGroups]);

  const purchasableGroups = useMemo(() => {
    return fulfillmentGroups.filter(
      (group) =>
        canPurchaseLabelStatus(group.status) && group.selectedShippingRate,
    );
  }, [fulfillmentGroups]);

  const currentAction = useRef<FulfillmentGroupActions | null>(null);
  const displayedButtonAction = canPurchaseEveryGroup
    ? FulfillmentGroupActions.CREATE_AND_PRINT_LABEL
    : FulfillmentGroupActions.PRINT_LABEL;

  const performAction = useHandler((action: FulfillmentGroupActions) => {
    // Log potential double-calls when action is already in progress
    if (actionLoad.pending) {
      console.error(
        "Detected double-call to performAction while action is pending",
        { action, currentAction: currentAction.current },
      );
    }

    currentAction.current = action;

    const executeAction = () => {
      void doAction();

      // If the search value was scanned, clear the search field
      const url = new URL(location.href);
      const searchValue = url.searchParams.get("search")?.trim();

      if (searchValue?.startsWith("FF_")) {
        setSearchRefreshSymbol?.(Symbol());
      }
    };

    if (
      (action === FulfillmentGroupActions.CREATE_LABEL ||
        action === FulfillmentGroupActions.CREATE_AND_PRINT_LABEL) &&
      totalRatePrice !== null &&
      totalRatePrice !== undefined
    ) {
      executeWithPinCheck(
        executeAction,
        { type: PinVerificationType.ExpensiveLabel, labelCost: totalRatePrice },
        {
          title: "Expensive label PIN verification",
          description:
            "Please enter your PIN to verify that you want to purchase an expensive label",
        },
      );
    } else {
      executeAction();
    }
  });

  const startReturn = useHandler((orderId: string) => {
    navigate(
      `/stores/${team?._id}/order-management/returns/create-return/${orderId}`,
    );
  });

  const openUnmergeModal = useHandler(() => {
    setUnmergeModalOpen(true);
  });

  const isEditingLocked = fulfillmentGroups.some((group) =>
    isEditingLockedStatus(group.status),
  );

  const handleUnmerge = useHandler(async (fulfillmentOrderIds: string[]) => {
    try {
      if (fulfillmentGroups.length !== 1 || !fulfillmentGroups[0]) {
        return;
      }

      const fulfillmentGroup = fulfillmentGroups[0];
      const totalFulfillmentOrders =
        fulfillmentGroup.orders?.filter((o) => o.fulfillmentOrderExternalId)
          .length ?? 0;

      // If all fulfillment orders are selected to be merged, just unmerge the whole group as normal
      if (fulfillmentOrderIds.length === totalFulfillmentOrders) {
        await client.unmergeFulfillmentGroups({
          fulfillmentGroupIds: [fulfillmentGroup._id],
        });
      } else {
        await client.unmergeFulfillmentOrdersFromGroups({
          unmergeRequests: [
            {
              fulfillmentGroupId: fulfillmentGroup._id,
              fulfillmentOrderIdsToUnmerge: fulfillmentOrderIds,
            },
          ],
        });
      }

      toast("Successfully unmerged shipments", { variant: "success" });
      triggerRefresh();
    } catch (error) {
      toast("Error unmerging shipments", { variant: "error" });
    }
  });

  const selectedPackingSlipSizes = useSelectedPackingSlipSizes(
    packingSlips,
    fulfillmentGroups,
    team,
  );

  const selectedPersonalizedSlipSizes = useSelectedPersonalizedSlipSizes(
    personalizedSlips,
    fulfillmentGroups,
  );

  const openMissingItemsModal = useHandler(() => {
    if (fulfillmentGroups.length === 1 && fulfillmentGroups[0]) {
      missingItemsModal.openModal(fulfillmentGroups[0], {
        onFulfillmentGroupUpdated: () => {
          triggerRefresh();
        },
      });
    }
  });

  const [cancelFreightLoad, triggerCancelFreight] = useTriggerLoad(
    async (signal) => {
      try {
        const groupId = fulfillmentGroups[0]?._id;
        if (!groupId) {
          return;
        }
        await client.cancelFreightBooking(
          { fulfillmentGroupId: groupId },
          { signal },
        );
        toast("Freight booking cancelled", { variant: "success" });
        triggerRefresh();
      } catch (error) {
        toast("Failed to cancel freight booking", { variant: "error" });
      }
    },
  );

  const createPurchaseOrder = useHandler(async (fulfillmentGroupId: string) => {
    if (!inventoryRpcClient) {
      toast("Inventory module is not available", {
        variant: "error",
        duration: Temporal.Duration.from({ seconds: 3 }),
      });
      return;
    }
    try {
      const result = await inventoryRpcClient.createPurchaseOrder({
        fulfillmentGroupId,
      });
      navigate(
        `${routes.inventoryManagementBaseUrl}/purchase-orders/all/${result.purchaseOrderId}`,
      );
    } catch (error) {
      console.error("Failed to create purchase order:", error);
      toast("Failed to create purchase order", {
        variant: "error",
        duration: Temporal.Duration.from({ seconds: 3 }),
      });
    }
  });

  const viewPurchaseOrder = useHandler((purchaseOrderId: string) => {
    navigate(
      `${routes.inventoryManagementBaseUrl}/purchase-orders/all/${purchaseOrderId}`,
    );
  });

  const [actionLoad, doAction] = useTriggerLoad(
    async (signal?: AbortSignal) => {
      await doOMSAction(
        {
          fulfillmentDate,
          action: currentAction.current,
          user,
          client,
          fulfillmentGroups,
          team,
          notifyMarketplace,
          resetSelections,
          onGroupsFulfillment,
          reloadBalance,
          triggerRefresh,
          printBatch: batchPrinting.printBatch,
          openMarkAsFulfilledModal,
          addFunds: onAddFunds,
          placeOnHold: onPlaceOnHold,
          editManualOrder: onEditManualOrder,
          mixedPackingSlipSizes: selectedPackingSlipSizes.size > 1,
          mixedPersonalizedSlipSizes: selectedPersonalizedSlipSizes.size > 1,
          submitCarrierClaim: onSubmitCarrierClaim,
          startReturn,
          openPackingSlipBinModal,
          createReturnLabel,
          openCancelOrderModal,
          openOverrideNotReceivedModal: () =>
            setOverrideNotReceivedModalOpen(true),
          openUnmergeModal,
          openMissingItemsModal,
          openFreightBookingModal: () => setFreightBookingModalOpen(true),
          createPurchaseOrder,
          viewPurchaseOrder,
          navigateToScanAndVerify: (fulfillmentGroupId: string) => {
            navigate(
              `/stores/${team?._id}/order-management/outbound-labels/scan/${fulfillmentGroupId}`,
            );
          },
          purchasableGroups,
          deselectNonPurchasableGroups: deselectGroups,
          openBinSelectionModal,
          openInventoryAdjustmentWarningModal,
          openInsufficientInventoryModal,
          executeWithPinCheck,
          deviceLocationId,
        },
        signal,
      );
      if (
        currentAction.current === FulfillmentGroupActions.CREATE_LABEL ||
        currentAction.current === FulfillmentGroupActions.CREATE_AND_PRINT_LABEL
      ) {
        setNotifyMarketplace(true);
        setFulfillmentDate(null);
      }
    },
  );

  const { onSelectedRateChange } = useRateActions({
    fulfillmentGroups,
    updateRows,
    setSelectedPreset,
    openEditAddressModal,
    onEditAddress,
    onUseAutoCleanedAddress,
  });

  const {
    selectedRate,
    mixedRates,
    deliveryMethod,
    availableRates,
    selectedParcelsPerGroup,
    mixedParcels,
    mixedWeight,
    canBulkEditParcels,
    assignedUser,
    mixedUsers,
    totalWeight,
    ratesPending,
    totalCustomsDeclarationWeight,
    destinationAddress,
    originAddress,
    totalRatePrice,
    totalChargedToRedoBalance,
    trackingNumbers,
    allowMultipleParcels,
  } = useGroupInfo({
    fulfillmentGroups,
    parcelValues,
    availableUsers,
    permissionsToEditEveryGroup,
    team,
  });

  const parcelBeingEditedPerGroup = selectedParcelsPerGroup.map(
    (parcels) => parcels[parcelToBeEdited] ?? parcels[0]!,
  );

  const { usedWeightLearning, usedPackageLearning } =
    useFulfillmentGroupUsedLearning(fulfillmentGroups);

  const [duplicateShipmentLoad, triggerCreateDuplicateShipment] =
    useTriggerLoadWithInput(
      async (lineItemExternalIds: string[] | undefined, signal) => {
        try {
          const response = await client.duplicateShipments(
            {
              fulfillmentGroupIds: fulfillmentGroups.map((fg) => fg._id),
              lineItemExternalIdsToInclude: lineItemExternalIds,
            },
            { signal },
          );
          setSelectedFulfillmentGroups?.(response.newGroups);
        } catch (err) {
          toast("Failed to Reship", { variant: "error" });
        }
      },
    );

  const [voidShipmentLoad, triggerVoidShipmentLoad] = useTriggerLoad(
    async (signal) => {
      try {
        await client.voidShipments(
          { fulfillmentGroupIds: fulfillmentGroups.map((fg) => fg._id) },
          { signal },
        );

        onGroupsVoided(fulfillmentGroups.map((fg) => fg._id));
      } catch (err) {
        const msg =
          fulfillmentGroups.length > 1
            ? `Failed to void ${fulfillmentGroups.length} labels`
            : "Failed to void label";
        toast(msg, { variant: "error" });
      }

      return;
    },
  );

  const [_, triggerRefreshRates] = useTriggerLoad(async (signal) => {
    try {
      toast("Getting updated rates...", { variant: "success" });
      const BATCH_SIZE = 5;

      const batches = fulfillmentGroups.reduce((acc, group, index) => {
        const batchIndex = Math.floor(index / BATCH_SIZE);
        if (!acc[batchIndex]) {
          acc[batchIndex] = [];
        }
        acc[batchIndex].push(group._id);
        return acc;
      }, [] as string[][]);

      for (const batch of batches) {
        await client.refreshShippingRates(
          { fulfillmentGroupIds: batch },
          { signal },
        );
      }
    } catch (err) {
      toast("Failed to refresh rates", { variant: "error" });
    }
  });

  // If all the fulfillment groups have the same shipping title, use that.
  // Otherwise, show "-"
  const requestedShippingService = useMemo(() => {
    return fulfillmentGroups.reduce(
      (commonName, fulfillmentGroup) => {
        if (
          commonName === "" ||
          commonName === getCustomerShippingMethod(fulfillmentGroup)
        ) {
          return commonName;
        }
        return "-";
      },
      getCustomerShippingMethod(fulfillmentGroups[0]) || "-",
    );
  }, [fulfillmentGroups]);

  const needsFunds = calculateNeedsFunds(
    balance,
    totalChargedToRedoBalance,
    canPurchaseEveryGroup,
    team?.settings?.outboundLabels?.billing?.autoReload?.floor ?? 0,
  );

  const onChangeUseAutoCleanedAddress = useHandler(
    async (useAutoCleanedAddress: boolean) => {
      if (fulfillmentGroups.length !== 1 || !fulfillmentGroups[0]) {
        return;
      }
      await client.setUseOriginalAddress({
        fulfillmentGroupId: fulfillmentGroups[0]._id,
        useOriginalAddress: !useAutoCleanedAddress,
      });
    },
  );

  const {
    errorMessage: groupErrorMessage,
    warningMessage: groupWarningMessage,
    voidConfirmationMessage,
  } = actionMessages({
    currentValue: displayedButtonAction,
    fulfillmentGroups,
    selectedParcels: selectedParcelsPerGroup[0] ?? [],
    mixedParcels,
    totalRatePrice,
    needsFunds,
    balance,
    balanceLoading,
    selectedPreset,
    selectedRate,
    deliveryMethod,
    ratesPending,
    totalCustomsDeclarationWeight,
    totalWeight,
    destinationAddress,
    originAddress,
  });

  const errorMessage: string =
    groupErrorMessage +
    (fulfillmentGroups
      .map((group) => group.providerActionMessages?.errorMessage)
      .filter(Boolean)
      .join(" ") ?? "");

  const warningMessage: string =
    groupWarningMessage +
    (fulfillmentGroups
      .map((group) => group.providerActionMessages?.warningMessage)
      .filter(Boolean)
      .join(" ") ?? "");

  const isOpenish = fulfillmentGroups.every(
    (group) => !isDoneStatus(group.status),
  );

  const showVoidLabelButton = fulfillmentGroups.every((group) =>
    canVoidLabel(group),
  );

  const showCancelFreightButton =
    fulfillmentGroups.length === 1 &&
    fulfillmentGroups[0] !== undefined &&
    canVoidFreight(fulfillmentGroups[0]);

  const { rateTableDescription, rateTableName } =
    useGetRequestedShippingServicePromisedDeliveryDate({ fulfillmentGroups });

  const hasMarketplaceNotification = fulfillmentGroups.some(
    (group) => group.marketplaceNotification,
  );

  const allGroupsLocalPickup = fulfillmentGroups.every(
    (group) => group.deliveryMethod === RedoShopifyDeliveryMethodType.PickUp,
  );

  const showParcelPackingSlipButtons =
    fulfillmentGroups.length === 1 &&
    isConsolidationShipment(fulfillmentGroups[0]!);
  const singleFulfillmentGroupId =
    fulfillmentGroups.length === 1 ? fulfillmentGroups[0]!._id : undefined;
  const hasShipmentMessages = fulfillmentGroups.some((group) =>
    Object.values(group.shipmentMessages ?? {}).some((messages) =>
      messages.some(
        (message) =>
          !message.ignored &&
          message.message != null &&
          message.message.length > 0,
      ),
    ),
  );

  const cardComponents: Record<
    string,
    { title: string; content: ReactNode; headerActions?: ReactNode }
  > = {
    "shipping-info": {
      title:
        fulfillmentGroups.length > 1 ? "Bulk update" : "Shipping information",
      content: (
        <FulfillmentShippingInfoCard
          availableRates={availableRates}
          displayedButtonAction={displayedButtonAction}
          everythingDisabled={everythingDisabled}
          fulfillmentDate={fulfillmentDate}
          fulfillmentGroups={fulfillmentGroups}
          mixedRates={mixedRates}
          onChangeUseAutoCleanedAddress={onChangeUseAutoCleanedAddress}
          onEditAddress={onEditAddress}
          onPresetSelect={onPresetSelect}
          onSelectedLocationChange={onSelectedLocationChange}
          onSelectedRateChange={onSelectedRateChange}
          parcelBeingEditedIndex={parcelToBeEdited}
          parcelBeingEditedPerGroup={parcelBeingEditedPerGroup}
          presets={presets}
          ratesPending={ratesPending}
          requestedShippingService={rateTableName ?? requestedShippingService}
          requestedShippingServiceDescription={rateTableDescription}
          selectedPreset={selectedPreset}
          selectedRate={selectedRate}
          selectedStatus={selectedStatus}
          updateParcel={updateParcelBeingEdited}
          updateRows={updateRows}
        />
      ),
    },
    "parcel-details": {
      title: `Packages (${selectedParcelsPerGroup[0]?.length || 1})`,
      content: allGroupsLocalPickup ? null : (
        <ParcelDetails
          disableBulkParcelEditing={!canBulkEditParcels}
          displayedButtonAction={displayedButtonAction}
          everythingDisabled={everythingDisabled}
          fulfillmentGroupId={singleFulfillmentGroupId}
          isEditingLocked={isEditingLocked}
          items={fulfillmentGroups[0]?.items ?? []}
          mixedParcels={mixedParcels}
          mixedWeight={mixedWeight}
          onAllocationsFlush={onAllocationsFlush}
          onParcelAdd={onParcelAdd}
          onParcelChange={onParcelChange}
          onParcelModalClose={() => setParcelModalOpen(false)}
          onParcelModalOpen={() => setParcelModalOpen(true)}
          onRemoveParcel={onRemoveParcel}
          parcelModalOpen={parcelModalOpen}
          parcelValues={parcelValues}
          selectedParcels={selectedParcelsPerGroup[0] ?? []}
          selectedRate={selectedRate}
          selectedStatus={selectedStatus}
          showParcelPackingSlipButtons={showParcelPackingSlipButtons}
          showSingleParcelTrackingNumber={showSingleParcelTrackingNumber}
          trackingNumbers={trackingNumbers}
          usedPackageLearning={usedPackageLearning}
          usedWeightLearning={usedWeightLearning}
        />
      ),
      headerActions: allowMultipleParcels ? (
        <ParcelDetailsHeaderActions
          allowMultipleParcels={allowMultipleParcels}
          isEditingLocked={isEditingLocked}
          onAddPackage={() => setParcelModalOpen(true)}
        />
      ) : undefined,
    },
    items: {
      title: `Items (${fulfillmentGroups.flatMap((group) => group.items).reduce((acc, item) => acc + item.quantity, 0)})`,
      content: (
        <FulfillmentItemsCard
          fulfillmentGroups={fulfillmentGroups}
          setRows={setRows}
        />
      ),
      headerActions: (
        <FulfillmentItemsCardHeaderActions
          fulfillmentGroups={fulfillmentGroups}
          setRows={setRows}
        />
      ),
    },
    "customs-declarations": {
      title: "Customs declarations",
      content: allGroupsLocalPickup ? null : (
        <FulfillmentCustomsDeclarationsCard
          customerTaxIds={
            fulfillmentGroups.length === 1
              ? (fulfillmentGroups[0]?.customerTaxIds ?? [])
              : []
          }
          fulfillmentGroups={fulfillmentGroups}
          isEditable={!everythingDisabled && !isEditingLocked}
          key={fulfillmentGroups.map((group) => group._id).join(",")}
          onUpdateCustomerTaxIds={
            fulfillmentGroups.length === 1
              ? (taxIds) => {
                  updateRows(
                    fulfillmentGroups.map(({ _id }) => ({
                      fulfillmentGroupId: _id,
                      customerTaxIds: taxIds,
                    })),
                  );
                }
              : undefined
          }
          updateGroups={updateRows}
        />
      ),
    },
    "additional-settings": {
      title: "Additional settings",
      content: (
        <AdditionalFulfillmentSettings
          disableBulkSettingsEditing={!canBulkEditParcels}
          everythingDisabled={everythingDisabled}
          fulfillmentDate={fulfillmentDate}
          fulfillmentGroups={fulfillmentGroups}
          isEditingLockedStatus={isEditingLocked}
          notifyMarketplace={notifyMarketplace}
          parcelBeingEditedPerGroup={parcelBeingEditedPerGroup}
          setFulfillmentDate={setFulfillmentDate}
          setNotifyMarketplace={setNotifyMarketplace}
          updateParcel={updateParcelBeingEdited}
          updateRows={updateRows}
        />
      ),
    },
    "order-information": {
      title: "Payment information",
      content:
        fulfillmentGroups.length === 1 && fulfillmentGroups[0] ? (
          <FulfillmentOrderInformationCard
            fulfillmentGroup={fulfillmentGroups[0]}
          />
        ) : null,
    },
    "assigned-user": {
      title: "Assigned user",
      content: (
        <UserSelectDropdown
          assignedUser={assignedUser}
          disabled={everythingDisabled || isEditingLocked}
          mixedUsers={mixedUsers}
          onUserChange={onUserChange}
          userValues={availableUsers}
        />
      ),
    },
    "packing-slip": {
      title: "Packing slip",
      content: (
        <FulfillmentPackingSlipTemplateCard
          everythingDisabled={everythingDisabled}
          fulfillmentGroups={fulfillmentGroups}
          isEditingLocked={isEditingLocked}
          updateRows={updateRows}
        />
      ),
    },
    "personalized-slip": {
      title: "Personalized insert",
      content: personalizedSlipsAccessible(team?.settings.outboundLabels) ? (
        <FulfillmentPersonalizedSlipTemplateCard
          canPurchaseEveryGroup={canPurchaseEveryGroup}
          everythingDisabled={everythingDisabled}
          fulfillmentGroups={fulfillmentGroups}
          updateRows={updateRows}
        />
      ) : null,
    },
    notes: {
      title: "Notes",
      content: (
        <FulfillmentNotesSlideout
          everythingDisabled={everythingDisabled}
          fulfillmentGroups={fulfillmentGroups}
          updateRows={updateRows}
        />
      ),
    },
    "parcel-photos": {
      title: "Parcel photos",
      content:
        fulfillmentGroups.length === 1 &&
        fulfillmentGroups[0] &&
        (team?.settings.outboundLabels.verifyParcelPhotos?.enabled ?? false) ? (
          <ParcelPhotosViewer fulfillmentGroupId={fulfillmentGroups[0]._id} />
        ) : null,
    },
    messages: {
      title: "Messages",
      content: hasShipmentMessages ? (
        <ShipmentMessagesCard fulfillmentGroups={fulfillmentGroups} />
      ) : null,
    },
    "marketplace-notification": {
      title: "Marketplace notification",
      content: hasMarketplaceNotification ? (
        <MarketplaceNotificationSection groups={fulfillmentGroups} />
      ) : null,
    },
  };

  return fulfillmentGroups?.length ? (
    <CarrierAccountsProvider>
      <>
        <BinSelectionModal {...binSelectionModalProps} />
        <InventoryAdjustmentWarningModal
          {...inventoryAdjustmentWarningModalProps}
        />
        <InsufficientInventoryModal {...insufficientInventoryModalProps} />
        <OverrideNotReceivedModal
          fulfillmentGroups={fulfillmentGroups}
          isOpen={overrideNotReceivedModalOpen}
          onClose={() => setOverrideNotReceivedModalOpen(false)}
        />
        <UnmergeModal
          fulfillmentGroup={fulfillmentGroups[0] ?? null}
          isOpen={unmergeModalOpen}
          onClose={() => setUnmergeModalOpen(false)}
          onUnmerge={handleUnmerge}
        />
        {fulfillmentGroups.length === 1 &&
          fulfillmentGroups[0] &&
          originAddress &&
          destinationAddress && (
            <FreightBookingModal
              balance={balance ?? "0"}
              customerReference1={fulfillmentGroups[0].orders[0]?.name}
              destinationAddress={
                (fulfillmentGroups[0]
                  .verifiedDestinationAddress as FulfillmentOrderAddress) ??
                destinationAddress
              }
              freightProducts={fulfillmentGroupToFreightProducts(
                fulfillmentGroups[0]!,
              )}
              isOpen={freightBookingModalOpen}
              key={`freight-${fulfillmentGroups[0]._id}`}
              onClose={() => setFreightBookingModalOpen(false)}
              onPurchase={handleFreightPurchase}
              originAddress={
                (fulfillmentGroups[0]
                  .verifiedOriginAddress as FulfillmentOrderAddress) ??
                originAddress
              }
            />
          )}
        <EditManualOrderSlideout
          fulfillmentGroupData={fulfillmentGroups[0]!}
          key={`edit-order-${fulfillmentGroups[0]!._id}`}
          onClose={() => setEditOrderSlideoutOpen(false)}
          open={editOrderSlideoutOpen}
          triggerRefresh={triggerRefresh}
        />
        <ConfirmEditAddressModal
          isOpen={confirmEditAddressModalAddress !== null}
          onClose={closeConfirmModal}
          onConfirm={onConfirmEditAddress}
        />
        <ParcelEditProvider
          parcelBeingEditedIndex={parcelToBeEdited}
          parcelsLength={selectedParcelsPerGroup[0]?.length ?? 1}
          setParcelBeingEditedIndex={setParcelToBeEdited}
        >
          <Flex
            bgColor="primary"
            className={ordersCss.drawerContent}
            dir="column"
            gap="none"
            inert={inert}
            overflowY="auto"
          >
            <div className={ordersCss.stickyHeaderWrapper}>
              {showHeader && (
                <Flex
                  align="center"
                  borderBottomWidth="1px"
                  borderColor="primary"
                  borderStyle="solid"
                  className={ordersCss.slideoutHeaderContainer}
                  dir="row"
                  justify="space-between"
                  pb="2xl"
                  pl="3xl"
                  pr="3xl"
                  pt="2xl"
                >
                  <Flex dir="column" gap="xs">
                    {isOpenish ? (
                      fulfillmentGroups.length > 1 ? (
                        <Text fontSize="xl" fontWeight="semibold">
                          Create {fulfillmentGroups.length} labels
                        </Text>
                      ) : (
                        <Text fontSize="xl" fontWeight="semibold">
                          Create label
                        </Text>
                      )
                    ) : (
                      <Text fontSize="xl" fontWeight="semibold">
                        Label detail
                      </Text>
                    )}
                  </Flex>
                  {onClose && (
                    <IconButton
                      data-testid="close-fulfillment-group-slideout"
                      onClick={onClose}
                      textColor="quinary"
                    >
                      <XIcon />
                    </IconButton>
                  )}
                </Flex>
              )}

              <Flex
                align="flex-start"
                borderBottomWidth="1px"
                borderColor="primary"
                borderStyle="solid"
                dir="row"
                justify="space-between"
              >
                <PinVerificationProvider>
                  <FulfillmentActionsCard
                    actionLoad={actionLoad}
                    cancelFreightLoad={cancelFreightLoad}
                    canPurchaseEveryGroup={canPurchaseEveryGroup}
                    displayedButtonAction={displayedButtonAction}
                    duplicateShipmentLoad={duplicateShipmentLoad}
                    errorMessage={errorMessage}
                    everythingDisabled={everythingDisabled}
                    externalActionButtonRef={externalActionButtonRef}
                    fulfillmentDate={fulfillmentDate}
                    fulfillmentGroups={fulfillmentGroups}
                    handleUpdates={updateRows}
                    insufficientFunds={needsFunds}
                    onActionButtonClick={onActionButtonClick}
                    performAction={performAction}
                    printShortcutsDisabled={printShortcutsDisabled}
                    purchasableGroups={purchasableGroups}
                    selectedStatus={selectedStatus}
                    showCancelFreightButton={showCancelFreightButton}
                    showCostSummary={showActionCostSummary}
                    showVoidLabelButton={showVoidLabelButton}
                    totalRatePrice={totalRatePrice}
                    triggerCancelFreight={triggerCancelFreight}
                    triggerCreateDuplicateShipment={
                      triggerCreateDuplicateShipment
                    }
                    triggerRefreshRates={triggerRefreshRates}
                    triggerVoidShipmentLoad={triggerVoidShipmentLoad}
                    updatesPending={updatesPending || ratesPending}
                    useDefaultActionButton={useDefaultActionButton}
                    voidConfirmationMessage={voidConfirmationMessage}
                    voidShipmentLoad={voidShipmentLoad}
                    warningMessage={warningMessage}
                  />
                </PinVerificationProvider>
              </Flex>
            </div>

            {(() => {
              const marketplaceNotificationCardId = "marketplace-notification";
              const visibleDraggableCardOrder = cardOrder.filter(
                (cardId) =>
                  cardId !== marketplaceNotificationCardId &&
                  !hiddenCardIdSet.has(cardId),
              );
              const marketplaceNotificationCard = hiddenCardIdSet.has(
                marketplaceNotificationCardId,
              )
                ? null
                : cardComponents[marketplaceNotificationCardId];
              const cardData: FulfillmentCardData[] = visibleDraggableCardOrder
                .map((cardId) => {
                  const card = cardComponents[cardId];
                  if (!card || !card.content) {
                    return null;
                  }

                  return {
                    id: cardId,
                    title: card.title,
                    content: card.content,
                    isExpanded: !cardPreferences.collapsedStates[cardId],
                    headerActions: card.headerActions,
                  };
                })
                .filter((card) => card !== null);

              return (
                <Flex dir="column" gap="xl" justify="space-between" w="full">
                  <div className="draggable-list-container">
                    <DraggableList<
                      FulfillmentCardData,
                      { onToggleExpanded: (id: string) => void },
                      DraggableFulfillmentCard
                    >
                      commonProps={{ onToggleExpanded: handleToggleExpanded }}
                      constrainDrag
                      itemKey={(item) => item.id}
                      list={cardData}
                      onMoveEnd={handleCardReorder}
                      padding={0}
                      template={DraggableFulfillmentCard}
                    />
                  </div>
                  {marketplaceNotificationCard?.content && (
                    <StaticFulfillmentCard
                      item={{
                        id: marketplaceNotificationCardId,
                        title: marketplaceNotificationCard.title,
                        content: marketplaceNotificationCard.content,
                        isExpanded:
                          !cardPreferences.collapsedStates[
                            marketplaceNotificationCardId
                          ],
                        headerActions:
                          marketplaceNotificationCard.headerActions,
                      }}
                      onToggleExpanded={handleToggleExpanded}
                    />
                  )}
                </Flex>
              );
            })()}
          </Flex>
        </ParcelEditProvider>
      </>
    </CarrierAccountsProvider>
  ) : null;
});

const StaticFulfillmentCard = memo(function StaticFulfillmentCard({
  item,
  onToggleExpanded,
}: {
  item: FulfillmentCardData;
  onToggleExpanded: (id: string) => void;
}) {
  const toggleExpanded = () => onToggleExpanded(item.id);
  const toggleExpandedFromKeyboard = (
    event: ReactKeyboardEvent<HTMLDivElement>,
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleExpanded();
    }
  };

  return (
    <Flex
      bgColor="primary"
      borderBottomWidth="1px"
      borderColor="secondary"
      borderLeftWidth="1px"
      borderRightWidth="1px"
      borderStyle="solid"
      borderTopWidth="none"
      dir="column"
      gap={item.isExpanded ? "lg" : "none"}
      pb="xl"
      pt="xl"
      px="3xl"
    >
      <Flex
        align="center"
        cursor="pointer"
        justify="space-between"
        onClick={toggleExpanded}
        onKeyDown={toggleExpandedFromKeyboard}
        role="button"
        tabIndex={0}
      >
        <Text color="secondary" fontSize="sm" fontWeight="semibold">
          {item.title}
        </Text>
        <Flex
          align="center"
          gap="md"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          {item.headerActions && <Flex>{item.headerActions}</Flex>}
          <RedoButton
            hierarchy="tertiary"
            IconLeading={item.isExpanded ? TriangleDownIcon : TriangleRightIcon}
            onClick={toggleExpanded}
            size="sm"
          />
        </Flex>
      </Flex>
      {item.isExpanded && item.content}
    </Flex>
  );
});
